/**
 * This is a temporary component which renders the correct product card for Gift Finder
 * based on the `NEXT_PUBLIC_FF_PRODUCT_CARDS_V2` feature flag. This will accommodate global split testing
 */

// Variant Card Components
import { SkeletonLoader as ProductCardV2Skeleton } from '@virginexperiencedays/components-v2/src/cards/ProductV2';

// Control Card Components
import { ProductCard } from '@virginexperiencedays/components-v2/src/cards/Product';
import { CarouselProductCard } from '@virginexperiencedays/components-v2/src/cards/CarouselProduct';
import { ProductCardSkeleton } from '@virginexperiencedays/components-v2/src/cards/Product/Skeleton';
import { useIsProductCardsV2, ProductGridCard } from '../search/ProductCard';

// Utils
import { mapDYItemToProductCard, mapDYItemToProductCardV2 } from './utils';
import { styles } from '../gift-finder/ProductGrid';

type Props = {
  index: number;
  /**
   * The type of product card to render
   */
  render: 'card' | 'carousel-card' | 'skeleton';
  /**
   * The item object to render in the product card
   */
  item?: unknown;
  /**
   * If true, the product card will render a single image for 'carousel-card'
   */
  singleImage?: boolean;
  /**
   * The function to be called when the product card is clicked
   */
  onClick?: () => void;
  trackingModule?: string;
};

export const ProductCardRenderer = ({
  render = 'card',
  item,
  singleImage,
  index,
  onClick,
  trackingModule,
}: Props) => {
  const isProductCardsV2 = useIsProductCardsV2();

  // handle variant product cards
  if (isProductCardsV2) {
    const props = mapDYItemToProductCardV2(item);
    if (render !== 'skeleton' && !props) return null;
    switch (render) {
      case 'card':
        return (
          <ProductGridCard
            index={index}
            item={{ ...props, onClick }}
            tracking={{ module: trackingModule || 'gf_results_handpicked' }}
            titleClassName={styles.productCard.title.v2}
          />
        );
      case 'carousel-card': {
        const images = singleImage ? props.images.slice(0, 1) : props.images;
        return (
          <ProductGridCard
            index={index}
            item={{ ...props, images, onClick }}
            tracking={{ module: trackingModule || 'gf_results_top4' }}
            titleClassName={styles.productCard.title.v2}
          />
        );
      }
      case 'skeleton':
        return <ProductCardV2Skeleton />;
      default:
        return null;
    }
  }

  // handle control product card
  const props = mapDYItemToProductCard(item);
  if (render === 'skeleton' && !props) return null;
  switch (render) {
    case 'card':
      return <ProductCard {...props} onClick={onClick} />;
    case 'carousel-card':
      return <CarouselProductCard {...props} onClick={onClick} />;
    case 'skeleton':
      return <ProductCardSkeleton />;
    default:
      return null;
  }
};
